

import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import { useNavigate } from 'react-router-dom';
import { Quote, Users, BookOpen, Heart, Star } from "lucide-react";
import { Card, CardContent } from '@/components/ui/card';

const About = () => {
  const navigate = useNavigate();
  
  // Handle login/signup clicks
  const handleLogin = () => {
    navigate('/auth/login');
  };

  const handleSignup = () => {
    navigate('/auth/signup');
  };
  
  return (
    <div className="bg-white min-h-screen">
      <Navigation onLogin={handleLogin} onSignup={handleSignup} />
      
      <div className="container mx-auto px-4 pt-32 pb-16 md:pt-40 md:pb-28 max-w-4xl">
        <h1 className="text-4xl font-bold mb-4 text-center">About The Mat Monster</h1>
        
        <div className="prose prose-lg max-w-none space-y-8 text-left">
          <div className="flex items-center mb-8">
            <div className="flex-1 h-px bg-neutral-200"></div>
            <div className="px-4 italic text-primary text-xl font-medium">"From beginning to new beginning; the journey is the destination"</div>
            <div className="flex-1 h-px bg-neutral-200"></div>
          </div>
          
          <p className="text-neutral-700">
            At The Mat Monster, we believe in more than just mastering technique—we believe in mastering yourself.
          </p>
          
          <p className="text-neutral-700">
            This program was born out of one simple truth: progress in jiu-jitsu is often slow, scattered, and overwhelming. 
            Most of us are left piecing together bits from classes, YouTube, and rolls that blur together. 
            That's not how real growth happens. So we built a better way—something that helps you actually see your progress, 
            understand your game, sharpen your learning, and most importantly take note of how far you've come.
          </p>
          
          <p className="text-neutral-700">
            But even more than that, this is about creating a place where jiu-jitsu makes sense. A place where the art becomes yours.
          </p>
          
          <h2 className="text-2xl font-bold mt-12 mb-6 text-center">What We Stand For</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="shadow-sm border border-neutral-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-full bg-primary/10">
                    <BookOpen className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg">Clarity Over Chaos</h3>
                </div>
                <p className="text-neutral-600">
                  We believe your journey should be mapped, not muddled. That's why we built tools that bring structure, 
                  organization, and flow to your development—without ever boxing you in.
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-sm border border-neutral-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-full bg-primary/10">
                    <Heart className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg">Personal Growth First</h3>
                </div>
                <p className="text-neutral-600">
                  Winning is great, but understanding yourself, facing your limits, and showing up anyway—that's the good stuff. 
                  We're here to support the version of you that walks off the mat a little better than the one who walked on.
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-sm border border-neutral-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-full bg-primary/10">
                    <Star className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg">Progress Over Perfection</h3>
                </div>
                <p className="text-neutral-600">
                  You don't need to be the best. You just need to get better. We celebrate every rep, every journal entry, 
                  every "aha" moment. They add up.
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-sm border border-neutral-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-full bg-primary/10">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg">Your Journey, Your Way</h3>
                </div>
                <p className="text-neutral-600">
                  Everyone's jiu-jitsu looks different. Whether you're a casual grappler, a competitor, or just trying to stick 
                  with it between work and life—we've got room for you here.
                </p>
              </CardContent>
            </Card>
          </div>
          
          <Card className="shadow-sm border border-neutral-200 mt-8">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-full bg-primary/10">
                  <Heart className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-bold text-lg">Built With Heart (and Sweat)</h3>
              </div>
              <p className="text-neutral-600">
                This app isn't a product of some big company—it's built by people who train, who've struggled with the same things you have, 
                and who genuinely want to see you thrive.
              </p>
            </CardContent>
          </Card>
          
          <div className="mt-12 bg-neutral-50 p-8 rounded-lg border border-neutral-200 relative">
            <Quote className="text-primary/20 h-12 w-12 absolute top-4 left-4" />
            <h3 className="text-xl font-bold mb-4 pl-10">A note from the founder</h3>
            <div className="pl-10">
              <p className="text-neutral-700 mb-4">
                "I want you to know—you already won the moment you stepped on the mat. From here on out, it's just about being a little better than yesterday. 
                Every time you show up, that's another small step forward. And I'm right here with you.
              </p>
              <p className="text-neutral-700 mb-4">
                Growth doesn't just happen in the grind—it happens in the rest, too. So take the time to pause, reflect, and reset. 
                Be conscientious and intentional with your choices, your training, and your plan. That's the path to success—not just on the mat, but in life.
              </p>
              <p className="text-neutral-700 mb-4">
                Wherever you are on your journey, I'm wishing you nothing but the best.
              </p>
              <p className="text-neutral-700">
                May you have tough training partners—and the perspective to appreciate them. Oss"
              </p>
              <p className="text-primary font-bold text-right mt-4">
                -Justin
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default About;
